'use client';

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { toggleVariants } from '@/lib/components/ui/toggle';
import { cn } from '@/lib/utils';

const toggleGroupVariants = cva('flex items-center justify-center', {
  variants: {
    appearance: {
      default: 'gap-1',
      pills: 'gap-2 p-1 bg-muted/50 rounded-lg',
      segments: 'bg-muted/30 rounded-lg p-1 gap-0',
      cards: 'gap-3 p-2',
    },
  },
  defaultVariants: {
    appearance: 'default',
  },
});

const toggleGroupItemVariants = cva('transition-all duration-200 ease-in-out relative overflow-hidden', {
  variants: {
    appearance: {
      default: 'data-[state=on]:bg-primary data-[state=on]:text-primary-foreground hover:scale-105 active:scale-95',
      pills: cn(
        'rounded-md shadow-sm hover:shadow-md',
        'data-[state=on]:bg-primary data-[state=on]:text-primary-foreground',
        'data-[state=on]:shadow-lg data-[state=on]:scale-105',
        'hover:scale-102 active:scale-95'
      ),
      segments: cn(
        'rounded-md first:rounded-l-md last:rounded-r-md',
        'data-[state=on]:bg-background data-[state=on]:text-foreground',
        'data-[state=on]:shadow-sm data-[state=on]:border data-[state=on]:border-red-500',
        'hover:bg-background/50'
      ),
      cards: cn(
        'rounded-lg border-2 border-transparent',
        'bg-card text-card-foreground shadow-sm',
        'data-[state=on]:border-primary data-[state=on]:bg-primary/5',
        'hover:shadow-md hover:scale-105',
        'min-h-[80px] flex-col gap-2 p-4'
      ),
    },
  },
  defaultVariants: {
    appearance: 'default',
  },
});

const toggleGroupIconVariants = cva('transition-transform duration-200', {
  variants: {
    size: {
      default: 'text-base',
      sm: 'text-sm',
      lg: 'text-lg',
    },
    appearance: {
      default: '',
      pills: '',
      segments: '',
      cards: 'text-lg',
    },
  },
  defaultVariants: {
    size: 'default',
    appearance: 'default',
  },
});

const toggleGroupTextVariants = cva('font-medium', {
  variants: {
    size: {
      default: 'text-sm',
      sm: 'text-xs',
      lg: 'text-base',
    },
    appearance: {
      default: '',
      pills: '',
      segments: '',
      cards: 'text-sm',
    },
  },
  defaultVariants: {
    size: 'default',
    appearance: 'default',
  },
});

const toggleGroupWrapperVariants = cva('flex items-center justify-center', {
  variants: {
    size: {
      default: 'gap-2',
      sm: 'gap-1.5',
      lg: 'gap-2.5',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const ToggleGroupContext = React.createContext<VariantProps<typeof toggleVariants> & { appearance?: 'default' | 'pills' | 'segments' | 'cards' }>({
  size: 'default',
  variant: 'default',
  appearance: 'default',
});

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleVariants> & {
      appearance?: 'default' | 'pills' | 'segments' | 'cards';
    }
>(({ className, variant, size, children, appearance = 'default', ...props }, ref) => {
  return (
    <ToggleGroupPrimitive.Root ref={ref} className={cn(toggleGroupVariants({ appearance }), className)} {...props}>
      <ToggleGroupContext.Provider value={{ variant, size, appearance }}>{children}</ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
});

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleVariants> & {
      icon?: React.ReactNode;
      description?: string;
    }
>(({ className, children, variant, size, icon, description, ...props }, ref) => {
  const context = React.useContext(ToggleGroupContext);
  const appearance = context?.appearance || 'default';
  const itemSize = context.size || size || 'default';

  const baseClasses = cn(
    toggleVariants({
      variant: context.variant || variant,
      size: itemSize,
    }),
    toggleGroupItemVariants({ appearance })
  );

  return (
    <ToggleGroupPrimitive.Item ref={ref} className={cn(baseClasses, className)} {...props}>
      <div className={toggleGroupWrapperVariants({ size: itemSize })}>
        {icon && <span className={toggleGroupIconVariants({ size: itemSize, appearance })}>{icon}</span>}
        <span className={toggleGroupTextVariants({ size: itemSize, appearance })}>{children}</span>
      </div>
      {description && appearance === 'cards' && <span className="text-xs text-muted-foreground text-center">{description}</span>}
    </ToggleGroupPrimitive.Item>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroup, ToggleGroupItem };
